import { useParams, useNavigate, Path } from "react-router-dom";
import { useSearch } from "@/hooks/useSearch";
import { type CellContext } from "@tanstack/react-table";
import Table, { TableControls } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import Button from "@/components/Button";
import AvatarDetails from "@/components/AvatarDetails";
import Avatar from "@/components/Avatar";
import SearchInput from "@/components/SearchInput";
import IconButton from "@/components/IconButton";
import usePagination from "@/hooks/usePagination";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { useQuery, useMutation } from "@apollo/client";
import { EVENT_LEADS_QUERY, CANCEL_EVENT_REGISTRATION_MUTATION } from "../../queries";
import { useEventDetailQueryDocument } from "../../useEventDetailQuery";
import Dialog from "@/components/Dialog";
import { useState, useEffect } from "react";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { toLocaleDate } from "@/lib/dateHelpers";
import { useTopActionBar, SideBar } from "@/layouts/AppLayout/TopActionBarContext";
import { Controller } from "react-hook-form";
import Progress from "@/components/Progress";
import { downloadCsv } from "../../utils";
import { RegistrationStatus } from "@/pages/events/[eventSlug]/types.ts";

interface LeadRow {
  id: string;
  name: string;
  pgaId: string;
  email: string;
  phone: string;
  zipCode: string;
  referralSource: string;
  submitted: string;
  profilePhoto?: string;
}

// CSV export configuration
const CSV_COLUMN_HEADERS = [
  "First Name",
  "Last Name",
  "PGA ID",
  "Email",
  "Phone",
  "Zip Code",
  "External ID",
  "Event Title"
];

const generateCsvData = (leads: LeadRow[], registrantData: any[], eventTitle: string) => {
  return leads.map((lead) => {
    // Find the corresponding registrant data for externalId
    const registrant = registrantData.find((reg: any) => reg != null && reg.id === lead.id);

    // Split name into first and last name
    const nameParts = lead.name.split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    return [
      firstName,
      lastName,
      lead.pgaId,
      lead.email,
      lead.phone,
      lead.zipCode,
      registrant?.externalId || "",
      eventTitle
    ];
  });
};

export default function LeadsPage() {
  const { eventSlug = "" } = useParams();
  const { isCompactLayout: isMobile } = useIsCompactLayout();
  const navigate = useNavigate();
  const { renderFooter, resetPagination } = usePagination();
  const searchField = "search";
  const { searchValue, control, reset } = useSearch(searchField);
  const [variables, setVariables] = useState<{ search?: string }>({});
  const filterActive = variables.search;

  const { Menu, setMenu } = useTableRowMenu();
  const { showSidebar } = useTopActionBar();

  const [removeRegistrationDialogOpen, setRemoveRegistrationDialogOpen] = useState(false);
  const [leadToRemove, setLeadToRemove] = useState<LeadRow | null>(null);

  useEffect(() => {
    resetPagination();
    setVariables(searchValue ? { search: searchValue } : {});
  }, [searchValue, resetPagination]);

  const [cancelEventRegistrationMutation, { loading: loadingCancelRegistration }] = useMutation(
    CANCEL_EVENT_REGISTRATION_MUTATION
  );

  // Cleanup leadToRemove when dialog closes
  useEffect(() => {
    if (!removeRegistrationDialogOpen) {
      setLeadToRemove(null);
    }
  }, [removeRegistrationDialogOpen]);

  const {
    data: leadsData,
    loading: leadsLoading,
    refetch: refetchLeads
  } = useQuery(EVENT_LEADS_QUERY, {
    variables: {
      slug: eventSlug,
      status: RegistrationStatus.REGISTERED,
      ...variables
    },
    errorPolicy: "all"
  });

  // Fetch event data to get the event title
  const eventDetailQuery = useEventDetailQueryDocument();

  const { data: eventData } = useQuery(eventDetailQuery, {
    variables: { slug: eventSlug }
  });

  const allRegistrants = leadsData?.eventRegistrants?.nodes || [];

  const leads = allRegistrants
    .filter((registrant: any) => registrant != null)
    .map((registrant: any) => {
      const primaryPhone =
        registrant.phoneNumbers?.find((phone: any) => phone.phoneType === "mobile")?.phoneFormatted ||
        registrant.phoneNumbers?.find((phone: any) => phone.phoneType === "home")?.phoneFormatted ||
        registrant.phoneNumbers?.[0]?.phoneFormatted ||
        "";

      const transformedLead = {
        id: registrant.id,
        name: registrant.registrantName || "Unknown",
        pgaId: registrant.pgaId || "Lead",
        email: registrant.email || "",
        phone: primaryPhone,
        zipCode: registrant.recruitmentLead?.zipcode || "",
        referralSource: registrant.recruitmentLead?.referralSource || "",
        submitted: registrant.registeredAt
          ? toLocaleDate(registrant.registeredAt, { year: "numeric", month: "short", day: "numeric" })
          : "N/A",
        profilePhoto: registrant.profilePhoto
      };

      return transformedLead;
    });

  const handleCsvExport = () => {
    downloadCsv(
      [
        CSV_COLUMN_HEADERS,
        ...generateCsvData(
          leads,
          allRegistrants.filter((reg: any) => reg != null),
          eventData?.event?.title || eventSlug
        )
      ],
      `event-leads-${eventSlug}-${Date.now()}.csv`
    );
    showSnackbar("Leads exported");
  };

  const handleViewPerson = (lead: LeadRow) => {
    navigate(`/people/${lead.pgaId}` as unknown as Path);
  };

  const handleAddInteraction = (lead: LeadRow) => {
    showSidebar(SideBar.CREATE_GLOBAL_INTERACTION, { pgaId: lead.pgaId })();
  };

  const handleRemoveRegistration = (lead: LeadRow) => {
    setLeadToRemove(lead);
    setRemoveRegistrationDialogOpen(true);
  };

  const columns: any[] = [
    ...(isMobile
      ? [
          {
            cell: (cellProps: CellContext<LeadRow, unknown>) => {
              const { profilePhoto } = cellProps.row.original;
              return <Avatar picture={profilePhoto} />;
            },
            id: "avatar"
          },
          {
            cell: (cellProps: CellContext<LeadRow, unknown>) => {
              const { name, pgaId, email, phone, referralSource, submitted } = cellProps.row.original;
              return (
                <div className="flex flex-col gap-1">
                  <div className="text-body-medium">{name}</div>
                  <div className="text-body-small text-sys-brand-on-surface-variant">PGA ID: {pgaId}</div>
                  {email && <div className="text-body-small text-sys-brand-on-surface-variant">{email}</div>}
                  {phone && <div className="text-body-small text-sys-brand-on-surface-variant">{phone}</div>}
                  {referralSource && (
                    <div className="text-body-small text-sys-brand-on-surface-variant">Source: {referralSource}</div>
                  )}
                  <div className="text-body-small text-sys-brand-on-surface-variant">Submitted: {submitted}</div>
                </div>
              );
            },
            id: "leadName"
          }
        ]
      : [
          {
            accessorKey: "name",
            header: "Name",
            cell: ({ row }: CellContext<LeadRow, unknown>) => (
              <AvatarDetails title={row.original.name} text={row.original.pgaId} picture={row.original.profilePhoto} />
            )
          }
        ]),
    ...(isMobile
      ? []
      : [
          {
            accessorKey: "email",
            header: "Email",
            cell: ({ row }: CellContext<LeadRow, unknown>) => row.original.email
          },
          {
            accessorKey: "phone",
            header: "Phone",
            cell: ({ row }: CellContext<LeadRow, unknown>) => row.original.phone
          },
          {
            accessorKey: "zipCode",
            header: "Zip Code",
            cell: ({ row }: CellContext<LeadRow, unknown>) => row.original.zipCode
          },
          {
            accessorKey: "referralSource",
            header: "Referral Source",
            cell: ({ row }: CellContext<LeadRow, unknown>) => row.original.referralSource
          },
          {
            accessorKey: "submitted",
            header: "Submitted",
            cell: ({ row }: CellContext<LeadRow, unknown>) => row.original.submitted
          }
        ]),
    {
      cell(cellProps: CellContext<LeadRow, unknown>) {
        const lead = cellProps.row.original;

        const actions: Action[] = [
          {
            text: "View Person",
            section: "Actions",
            onClick: () => handleViewPerson(lead)
          },
          {
            text: "Add Interaction",
            section: "Actions",
            onClick: () => handleAddInteraction(lead)
          },
          {
            text: "Remove Registration",
            iconName: "delete",
            destructive: true,
            onClick: () => handleRemoveRegistration(lead)
          }
        ];

        return (
          <div
            className="text-right flex flex-row justify-end"
            data-testid="menu-icon"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <IconButton
              id={actionId(cellProps.row.id)}
              onClick={() => setMenu({ rowId: cellProps.row.id, actions })}
              data-testid="actions"
              title="actions"
              name="more_vert"
              skipMargin
            />
          </div>
        );
      },
      id: "actions",
      size: 100
    }
  ];

  const removeRegistration = async () => {
    if (!leadToRemove) return;

    try {
      const { data } = await cancelEventRegistrationMutation({
        variables: {
          id: leadToRemove.id,
          issueRefund: true,
          suspend: false
        }
      });

      if (data?.cancelEventRegistration?.success) {
        showSnackbar(`Registration removed for ${leadToRemove.name}`);

        // Close the dialog and reset state
        setRemoveRegistrationDialogOpen(false);
        setLeadToRemove(null);

        // Refresh the leads list after successful removal
        refetchLeads();
      } else {
        const apiMessage = data?.cancelEventRegistration?.message;
        if (apiMessage) {
          showErrorSnackbar(apiMessage);
        } else {
          showErrorSnackbar("Registration not removed", { subtitle: "Please try again later." });
        }
      }
    } catch (error) {
      console.error("Error removing registration:", error);
      showErrorSnackbar("Registration not removed", { subtitle: "Please try again later." });
    }
  };

  return (
    <>
      <Menu />
      <div className="pt-12" />
      <Table
        data={leads}
        columns={columns}
        loading={leadsLoading}
        hasActiveFilters={!!filterActive}
        renderFilteredEmptyState={() => (
          <EmptyState
            title={filterActive ? "No Results Found" : "No Leads Found"}
            caption={
              filterActive
                ? `Try a different search or filter`
                : leadsData
                  ? "This event does not have any leads"
                  : "Loading leads..."
            }
            iconName="search"
          />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            actions={[
              {
                text: "Export Current List",
                iconName: "download",
                variant: "text",
                onClick: handleCsvExport
              }
            ]}
            activeFilters={filterActive ? 1 : 0}
            onClearAllFilters={() => {
              resetPagination();
              setVariables({});
              reset({ search: "" });
            }}
            onSortClick={onSortClick}
          >
            {(leads.length > 0 || searchValue !== "") && (
              <Controller
                name={searchField}
                control={control}
                render={({ field }) => (
                  <SearchInput
                    placeholder="Search Leads"
                    query={field.value}
                    onChange={field.onChange}
                    onClear={reset}
                  />
                )}
              />
            )}
          </TableControls>
        )}
        renderFooter={renderFooter(
          {
            edges: leads.map((lead) => ({ node: lead })),
            pageInfo: { hasNextPage: false, hasPreviousPage: false },
            totalCount: leadsData?.eventRegistrants?.totalCount || leads.length
          },
          { pageCount: leads.length }
        )}
      />

      <Dialog
        open={removeRegistrationDialogOpen}
        onClosed={() => setRemoveRegistrationDialogOpen(false)}
        headline="Remove Registration"
        actions={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setRemoveRegistrationDialogOpen(false)} variant="text">
              Cancel
            </Button>
            <Button
              onClick={removeRegistration}
              variant="filled"
              icon={loadingCancelRegistration ? <Progress size={20} color="white" /> : "delete"}
              theme="error"
              disabled={loadingCancelRegistration}
            >
              {loadingCancelRegistration ? "Removing..." : "Remove"}
            </Button>
          </div>
        }
      >
        <div className="py-4">
          <p className="text-body-medium text-sys-brand-on-surface">
            Are you sure you want to remove this lead from this event? This cannot be undone.
          </p>
          <p className="text-body-medium text-sys-brand-on-surface mt-2">
            <strong>Note:</strong> The person will not be deleted in the overall system.
          </p>
        </div>
      </Dialog>
    </>
  );
}
